<template>
  <div
    v-if="isOrderActionEnabled(order)"
    class="v2-order-actions"
  >
    <!-- Add Products button -->
    <v2-button
      v-if="order.shouldAddProducts && isBrand"
      variant="outline"
      @click="showAddProductsModal = true"
    >
      Add Products
    </v2-button>

    <!-- Modify button with export/import dropdown -->
    <template v-if="isModifyButtonEnabled(order)">
      <div
        v-if="isModifyOrderImportExportEnabled && !isModify"
        class="v2-order-actions__modify-split"
      >
        <v2-button
          variant="outline"
          @click="handleEnableModify(false)"
        >
          Modify
        </v2-button>
        <div class="v2-order-actions__dropdown-wrapper">
          <v2-button
            variant="ghost"
            size="sm"
            class="v2-order-actions__dropdown-trigger"
            @click="showModifyDropdown = !showModifyDropdown"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </v2-button>
          <div
            v-if="showModifyDropdown"
            v-click-outside="() => showModifyDropdown = false"
            class="v2-order-actions__dropdown"
          >
            <button
              class="v2-order-actions__dropdown-item"
              @click="showModifyDropdown = false; $emit('export-order-for-modify')"
            >
              Export Order
            </button>
            <button
              class="v2-order-actions__dropdown-item"
              @click="showModifyDropdown = false; $emit('open-import-modal')"
            >
              Import Order
            </button>
          </div>
        </div>
      </div>
      <v2-button
        v-else
        variant="outline"
        @click="handleEnableModify(false)"
      >
        {{ isModify ? 'Revert changes' : 'Modify' }}
      </v2-button>
    </template>

    <!-- Main action button -->
    <v2-button
      variant="primary"
      :disabled="isModify && hasError"
      @click="handleOpenActionModal(selectedAction.action)"
    >
      {{ selectedAction.text }}
    </v2-button>

    <!-- Additional actions dropdown -->
    <div
      v-if="hasDropdownActions"
      class="v2-order-actions__dropdown-wrapper"
    >
      <v2-button
        variant="ghost"
        size="sm"
        class="v2-order-actions__dropdown-trigger"
        @click="showDropdown = !showDropdown"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </v2-button>
      <div
        v-if="showDropdown"
        v-click-outside="() => showDropdown = false"
        class="v2-order-actions__dropdown"
      >
        <button
          v-if="isApproveEnabled(order) && !isModify"
          class="v2-order-actions__dropdown-item"
          @click="handleDropdownAction(ORDER_STATUS.CONFIRMED)"
        >
          {{ approveActionText }}
        </button>
        <!-- KP-4363: confirm the products ticked in the products list -->
        <button
          v-if="canConfirmSelectedProducts"
          class="v2-order-actions__dropdown-item"
          :disabled="loading"
          @click="showDropdown = false; openConfirmSelectedModal()"
        >
          Confirm selected products ({{ selectedProductIds.length }})
        </button>
        <template v-if="isModifyButtonEnabled(order) && isModify">
          <button
            class="v2-order-actions__dropdown-item"
            @click="handleDropdownAction(SAVE)"
          >
            Save
          </button>
          <button
            v-if="isModifyProductsActionAllowed(order)"
            class="v2-order-actions__dropdown-item"
            @click="handleDropdownAction(MODIFY)"
          >
            Modify Order
          </button>
          <button
            v-if="isModifyProductsActionAllowed(order)"
            class="v2-order-actions__dropdown-item"
            @click="handleDropdownAction(MODIFY_AND_CONFIRM)"
          >
            Modify & Confirm Order
          </button>
        </template>
        <button
          v-if="isCancelEnabled(order)"
          class="v2-order-actions__dropdown-item v2-order-actions__dropdown-item--danger"
          @click="handleDropdownAction(ORDER_STATUS.CANCELLED)"
        >
          Cancel
        </button>
        <button
          v-if="isRejectEnabled(order) && !isModify"
          class="v2-order-actions__dropdown-item v2-order-actions__dropdown-item--danger"
          @click="handleDropdownAction(ORDER_STATUS.REJECTED)"
        >
          Reject
        </button>
      </div>
    </div>

    <!-- Action confirmation modal -->
    <v2-modal
      :show="showActionModal"
      :title="modalTitle"
      @close="showActionModal = false"
    >
      <p>{{ modalBody }}</p>
      <label
        v-if="action === SAVE"
        class="v2-order-actions__checkbox"
      >
        <input
          v-model="sendEmailToRetailer"
          type="checkbox"
        >
        <span>Send notification to retailer</span>
      </label>
      <template #footer>
        <v2-button
          variant="outline"
          @click="showActionModal = false"
        >
          Cancel
        </v2-button>
        <v2-button
          variant="primary"
          :disabled="loading"
          @click="confirmAction(action)"
        >
          {{ loading ? 'Confirming...' : 'Confirm' }}
        </v2-button>
      </template>
    </v2-modal>

    <!-- Expected availability date modal -->
    <v2-modal
      :show="showDateModal"
      title="Please select estimated delivery window"
      @close="showDateModal = false"
    >
      <div class="v2-order-actions__date-section">
        <span class="v2-font-semibold">Estimated delivery Window:</span>
        <v2-date-range-picker
          :value="expectedDate"
          :config="{ dateFormat: 'M Y', mode: 'single' }"
          @change="onExpectedDateChange"
        />
      </div>
      <template #footer>
        <v2-button
          variant="outline"
          @click="showDateModal = false"
        >
          Cancel
        </v2-button>
        <v2-button
          variant="primary"
          :disabled="loading || !expectedDate"
          @click="handleSaveDate"
        >
          {{ loading ? 'Saving...' : 'Save' }}
        </v2-button>
      </template>
    </v2-modal>

    <!-- Add Products modal -->
    <v2-modal
      :show="showAddProductsModal"
      title="Add Products Window"
      size="full"
      @close="showAddProductsModal = false"
    >
      <v2-add-order-products
        :collection-id="order.collectionId"
        :order="order"
        @products-added="onProductsAdded"
      />
    </v2-modal>
  </div>
</template>

<script>
import V2Button from '@/components/v2/V2Button.vue'
import V2Modal from '@/components/v2/V2Modal.vue'
import V2DateRangePicker from '@/components/v2/V2DateRangePicker.vue'
import { useOrderActions, isAcceptModificationsOrder } from '@/composables/useOrderActions'
import { useOrderPartialConfirm } from '@/composables/useOrderPartialConfirm'
import UserRoleMixin from '@/views/apps/UserRoleMixin.vue'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'
import { formatObject } from '@/@core/utils/utils'
import { apiToastWarning } from '@/@core/utils/toast'
import V2AddOrderProducts from './V2AddOrderProducts.vue'

const clickOutside = {
  bind(el, binding) {
    el._clickOutside = event => {
      if (!el.contains(event.target)) {
        binding.value()
      }
    }
    // Defer listener registration to the next macrotask so the current
    // click event (that triggered the dropdown to open) finishes first
    // and doesn't immediately close the dropdown.
    el._clickOutsideTimer = setTimeout(() => {
      document.addEventListener('click', el._clickOutside)
    }, 0)
  },
  unbind(el) {
    clearTimeout(el._clickOutsideTimer)
    document.removeEventListener('click', el._clickOutside)
  },
}

export default {
  name: 'V2OrderActions',
  components: {
 V2Button, V2Modal, V2DateRangePicker, V2AddOrderProducts,
},
  directives: { clickOutside },
  mixins: [UserRoleMixin],
  props: {
    order: {
      type: Object,
      default: () => null,
    },
    originalOrder: {
      type: Object,
      default: () => null,
    },
    totalUnits: {
      type: Number,
      default: 0,
    },
    modifyUsingImport: {
      type: Boolean,
      default: false,
    },
    isDirectBrand: {
      type: Boolean,
      default: false,
    },
    loadOrder: {
      type: Function,
      default: () => Promise.resolve(),
    },
    isModifyEnabledProp: {
      type: Boolean,
      default: false,
    },
    isInDraft: {
      type: Boolean,
      default: false,
    },
    importedProductStatuses: {
      type: Array,
      default: () => [],
    },
    // KP-4363: product ids the brand ticked in the products list
    selectedProductIds: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const actions = useOrderActions()
    actions.initOrderActions(props.order, props.isModifyEnabledProp)
    const { isPartialConfirmEnabled, confirmSelectedProducts } = useOrderPartialConfirm()
    return { ...actions, isPartialConfirmEnabled, confirmSelectedProducts }
  },
  data() {
    return {
      showDropdown: false,
      showModifyDropdown: false,
      showActionModal: false,
      showDateModal: false,
      showAddProductsModal: false,
    }
  },
  computed: {
    canApprove() {
      return this.isApproveEnabled(this.order) && !this.isModify
    },
    canModify() {
      return this.isModifyButtonEnabled(this.order) && this.isModify
    },
    canCancel() {
      return this.isCancelEnabled(this.order)
    },
    canReject() {
      return this.isRejectEnabled(this.order) && !this.isModify
    },
    canConfirmSelectedProducts() {
      return this.isPartialConfirmEnabled(this.order) && !this.isModify
    },
    hasDropdownActions() {
      return this.canApprove || this.canModify || this.canCancel || this.canReject
        || this.canConfirmSelectedProducts
    },
  },
  watch: {
    modifyUsingImport(val) {
      if (val) {
        this.handleEnableModify(val)
      }
    },
    sendEmailToRetailer(newValue) {
      if (this.action === this.SAVE) {
        analytics.track(
          constants.TRACKS.ACTIONS.SINGLE_ORDER.TOGGLE_SEND_NOTIFICATION,
          formatObject({
            orderId: this.order.orderSeqId,
            collectionId: this.order.collectionId,
            sendEmailToRetailer: newValue,
          }),
        )
      }
    },
    order: {
      handler(orderData) {
        if (!orderData) return
        this.hasError = orderData.products?.some(p => p.hasError)
        if (!this.isModify && orderData.status) {
          this.initOrderActions(orderData, false)
        }
      },
      deep: true,
    },
  },
  methods: {
    handleEnableModify(enable) {
      this.enableModify(enable)
      if (this.isModify) {
        this.$emit('enable-modify-changes')
      } else {
        this.$emit('revert-modified-changes')
      }
    },
    onProductsAdded() {
      this.showAddProductsModal = false
      // The child component used to call loadOrder itself but did so without
      // the orderId, which could leave the page-level loader stuck. We now
      // own the reload here where the orderId is in scope.
      this.loadOrder(this.order?._id)
    },
    handleOpenActionModal(actionType) {
      const config = this.openActionModal(actionType, {
        order: this.order,
        originalOrder: this.originalOrder,
        importedProductStatuses: this.importedProductStatuses,
        isInDraft: this.isInDraft,
      })
      if (config) {
        this.showActionModal = true
      }
    },
    handleDropdownAction(actionType) {
      this.showDropdown = false
      this.handleOpenActionModal(actionType)
    },
    onExpectedDateChange(val) {
      this.expectedDate = val
    },
    handleSaveDate() {
      this.onClickSaveDate(this.order, this.order._id).then(result => {
        if (result?.shouldApprove) {
          this.approveOrderData(this.order._id, this.loadOrder)
        } else if (result?.shouldConfirmSelected) {
          this.handleConfirmSelectedData(this.order._id)
        }
        this.showDateModal = false
      })
    },
    openDateModal() {
      this.showDateModal = true
    },
    trackStatusChange(actionType) {
      const isModifyOrSave = actionType === this.MODIFY || actionType === this.SAVE
      if (isModifyOrSave || actionType === this.CONFIRM_SELECTED) return

      const trackName = this.isBrand
        ? constants.TRACKS.ACTIONS.BRAND_CHANGES_ORDER_STATUS
        : constants.TRACKS.ACTIONS.RETAILER_CHANGES_ORDER_STATUS
      analytics.track(
        trackName,
        formatObject({
          orderId: this.order.orderSeqId,
          collectionName: this.order.collectionName,
          selectedStatus: actionType,
        }),
      )
    },
    switchToDateModal() {
      this.showActionModal = false
      this.showDateModal = true
    },
    closeActionModalOnSuccess(result) {
      if (result?.success) {
        this.showActionModal = false
      }
    },
    // KP-4363: the retailer's Approve accepts the brand's changes
    handleConfirmApproveOrAccept(orderId) {
      if (isAcceptModificationsOrder(this.order)) {
        this.acceptOrderModifications(orderId, this.loadOrder).then(this.closeActionModalOnSuccess)
        return
      }
      this.handleConfirmApprove(orderId)
    },
    handleConfirmApprove(orderId) {
      if (this.isBrand && this.isModify) {
        apiToastWarning('You have some new changes. Please click modify before approve')
        return
      }

      const needsDeliveryWindow = this.isBrand && !this.order.expectedAvailabilityDate && !this.isDirectBrand
      if (needsDeliveryWindow) {
        apiToastWarning('Please Enter Estimated Delivery Window')
        this.switchToDateModal()
        return
      }

      this.approveOrderData(orderId, this.loadOrder).then(result => {
        if (result?.success) {
          this.showActionModal = false
          return
        }
        this.handleApproveError(result)
      })
    },
    isMissingAvailabilityDate(errData) {
      return this.isBrand
        && errData?.hasOwnProperty('isAvailableExAvDate')
        && !errData.isAvailableExAvDate
    },
    handleApproveError(result) {
      if (!result?.err) return

      const errData = result.err.response?.data?.data
      if (this.isMissingAvailabilityDate(errData)) {
        this.switchToDateModal()
      }
    },
    openConfirmSelectedModal() {
      if (!this.selectedProductIds.length) {
        apiToastWarning('Please select the products to confirm')
        return
      }
      const config = this.getConfirmSelectedModalConfig(this.selectedProductIds.length)
      this.modalTitle = config.title
      this.modalBody = config.body
      this.action = config.action
      this.showActionModal = true
    },
    handleConfirmSelected(orderId) {
      if (this.isModify) {
        apiToastWarning('You have some new changes. Please save them before confirming products')
        return
      }
      if (!this.order.expectedAvailabilityDate && !this.isDirectBrand) {
        apiToastWarning('Please Enter Estimated Delivery Window')
        this.switchToDateModal()
        return
      }
      this.handleConfirmSelectedData(orderId)
    },
    handleConfirmSelectedData(orderId) {
      this.loading = true
      this.confirmSelectedProducts(orderId, this.selectedProductIds, this.loadOrder).then(result => {
        this.loading = false
        if (result?.success) {
          this.showActionModal = false
          return
        }
        this.handleApproveError(result)
      })
    },
    handleConfirmCancel(orderId) {
      this.cancelOrderOrModifications(this.order, orderId, this.loadOrder).then(this.closeActionModalOnSuccess)
    },
    handleConfirmReject(orderId) {
      this.rejectOrder(orderId, this.loadOrder).then(this.closeActionModalOnSuccess)
    },
    handleConfirmSave(orderId) {
      this.saveOrder({
        orderId,
        order: this.order,
        originalOrder: this.originalOrder,
        importedProductStatuses: this.importedProductStatuses,
        loadOrder: this.loadOrder,
      }).then(this.closeActionModalOnSuccess)
    },
    handleConfirmModify() {
      // No synchronous enableModify(false) here: on success,
      // useOrderDetail#updateOrder reloads the order which flips
      // isLoaded=false, and V2OrderDetail's `v-if="!isLoaded"` unmounts
      // V2OrderActions — it remounts with fresh state, naturally
      // collapsing the dropdown back to "Modify Order". On failure we
      // want the dropdown to stay open so the user can retry, so the
      // optimistic reset would be net-harmful (it would desync from
      // the parent's isModifyEnabled which only resets on success).
      this.$emit('update-order')
      this.showActionModal = false
    },
    handleConfirmModifyAndConfirm() {
      // Estimated-delivery-window gate intentionally removed: Modify &
      // Confirm now fires the MODIFY_ORDER_PRODUCTS (shouldConfirmOrder=true)
      // call directly without prompting for a delivery window. The
      // plain "Confirm" path (handleConfirmApprove) still enforces the
      // date — we're only relaxing this specific Modify & Confirm flow.
      // (Same remount-on-success reasoning as handleConfirmModify above
      // — we don't optimistically clear modify mode here.)
      this.$emit('update-order', true)
      this.showActionModal = false
    },
    confirmAction(actionType) {
      const orderId = this.order._id

      const actionHandlers = {
        [this.ORDER_STATUS.CONFIRMED]: () => this.handleConfirmApproveOrAccept(orderId),
        [this.ORDER_STATUS.CANCELLED]: () => this.handleConfirmCancel(orderId),
        [this.ORDER_STATUS.REJECTED]: () => this.handleConfirmReject(orderId),
        [this.SAVE]: () => this.handleConfirmSave(orderId),
        [this.MODIFY]: () => this.handleConfirmModify(),
        [this.MODIFY_AND_CONFIRM]: () => this.handleConfirmModifyAndConfirm(),
        [this.CONFIRM_SELECTED]: () => this.handleConfirmSelected(orderId),
      }

      const handler = actionHandlers[actionType]
      if (handler) {
        handler()
      }
      this.trackStatusChange(actionType)
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-order-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &__modify-split {
    display: flex;
    align-items: center;
    gap: 0;
  }

  &__dropdown-wrapper {
    position: relative;
  }

  &__dropdown-trigger {
    padding: 0.25rem;
  }

  &__dropdown {
    position: absolute;
    right: 0;
    top: 100%;
    margin-top: 4px;
    min-width: 180px;
    // Floating glass — uses --v2-glass-surface-floating because this menu
    // can render over arbitrary content; black-tint glass would disappear.
    background: var(--v2-glass-surface-floating);
    backdrop-filter: blur(var(--v2-glass-blur-sm)) saturate(160%);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: 0.5rem;
    box-shadow: var(--v2-glass-shadow-floating);
    z-index: 100;
    overflow: hidden;
  }

  &__dropdown-item {
    display: block;
    width: 100%;
    padding: 0.5rem 0.75rem;
    text-align: left;
    background: none;
    border: none;
    font-size: 0.8125rem;
    color: var(--v2-foreground);
    cursor: pointer;

    &:hover {
      background: var(--v2-glass-surface-2);
    }

    &--danger {
      color: var(--v2-destructive);
    }
  }

  &__checkbox {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.75rem;
    font-size: 0.875rem;
    cursor: pointer;

    input {
      accent-color: var(--v2-primary);
    }
  }

  &__date-section {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
}
</style>
